// ════════════════════════════════════════════════════════════════════
// TERROR — X4 · SOLREND TOURNAMENTS
// ────────────────────────────────────────────────────────────────────
// "Bracketed in-client tournaments; faction wars may fold in here."
//                                                    — the roadmap, X4
//
// ── WHAT IS FINISHED AND WHAT IS NOT, SAID FIRST ─────────────────────
// FINISHED: the bracket. Seeding, byes, advancement, the walkover
// cascade, standings, and the Firebase shape — all pure, all proven by
// tournament.selftest.mjs, all usable today.
//
// NOT FINISHED, DELIBERATELY: the part where a pairing becomes a live
// duel. That is `onPairingReady` below, one function, and it needs the
// matchmaking lap that only caP'n can fly — two accounts, two browsers,
// a real handshake. Everything up to that seam is done, and the seam is
// named rather than faked, because a tournament that LOOKS live and
// cannot start a match is worse than one that says what it is.
//
// ── THE ONE DESIGN DECISION WORTH ARGUING ABOUT ──────────────────────
// A bracket is IMMUTABLE ROUNDS, not a linked tree. Round 0 is built
// once, complete with byes, and every later round is DERIVED from the
// winners of the one before it. Nothing is ever mutated in place except
// a match's `winner` field.
//
// Why: a tree that rewrites itself as results land is a tree where a
// wrong result cannot be corrected without rebuilding the thing, and
// where two clients reporting the same result can produce two different
// shapes. Here, a result is a single leaf write, the bracket is a pure
// function of the results so far, and correcting a mis-reported win is
// overwriting one field. It also means the whole bracket renders from
// one array with no recursion.
//
// ── SEEDING ──────────────────────────────────────────────────────────
// Standard bracket seeding — 1 plays the lowest seed, 2 plays the
// second-lowest, and the halves are built so 1 and 2 can only meet in
// the final. Byes go to the top seeds, which is the whole point of a
// bye. With 5 entrants the top 3 sit out round one; that is correct and
// it is what the selftest asserts.
//
// ── STORAGE ──────────────────────────────────────────────────────────
//   games/terror/tournaments/{id}/
//     meta     : { name, createdBy, createdAt, size, status, mode }
//     entrants : { [uid]: { name, seed, joinedAt } }
//     results  : { "r0m3": { winner: uid, code, at } }   ← FLAT, on purpose
//
// `results` is flat and keyed by round+match so a report is ONE write at
// a known path, which is what makes it safe for two clients to race:
// last writer wins and both wrote the same thing.
//
// ⚠ IT LIVES IN THE DRAWER, AND IT DID NOT USED TO. This shipped on a
// new TOP-LEVEL node and a wrong belief that IE_FIREBASE_LAW §1's
// new-field exemption covered it. §1 is about new fields under
// profiles/{uid} — which genuinely have no $extra guard — and says
// nothing about top-level nodes, which §2 forbids outright and which
// rules.json denies with a `"$other": { ".read": false, ".write": false }`
// catch-all. Every read and write here would have failed
// permission_denied on the live database, silently, because this layer
// is fire-and-forget by design. Moved under games/terror, where the
// $gameId wildcard already grants authed read+write — zero rules deploy.

import { ref, get, set, update, onValue, runTransaction } from "firebase/database";
import { db, GAME_BASE } from "./firebase";
import { awardCoins, spendCoins } from "./progression"; // ◕ the one lane Terror's own money moves through — see THE PAYOUT below

export const TOURNAMENT_SCHEMA = 2;
// v1 → v2 (2026-08-22, caP'n lifted the "single-player/1v1 only" decree):
// meta grew `bracketStyle`, `kind`, `entranceFee`. Old (v1) meta rows have
// none of those fields, and every reader below treats that absence as
// "classic1v1" + "free" — see normalizeBracketStyle and createTournament's
// `kind` default. `v` itself is a record for debugging/logs; nothing in
// this file branches on it, so bumping it breaks nothing already written.
//
// ── caP'n's ask, verbatim (paraphrased where noted) ───────────────────
// "the tournament: friends can create up to 8 players with all the same
// options as the multiplayer (as long as its even numbered and it makes
// sense, so 3 player free for all is out, but 4 player free for all is
// still valid, 1 tournament, 2 battles of 4 players each, 2 finalists
// remain and 1v1... gotta think out of the box) 2 tournament options
// Free Tournament (cheater decks allowed) Tournament of Kings (cannon
// decks only and you have all players pay an entrance fee, up to
// coordinator to set) winner get's most, 2nd place gets some, 3rd place
// gets a little of the winnings"
//
// Two independent axes came out of that:
//   BRACKET STYLE — the SHAPE of the rounds. classic1v1 (untouched,
//     still the default) or ffaGauntlet (new — see THE GAUNTLET STYLE
//     below). Not to be confused with gauntlet.js, the singleplayer
//     boss ladder — same word, unrelated system, named this because
//     "gauntlet" is what a run of FFA pools down to a final IS.
//   TOURNAMENT KIND — free vs. buy-in. Orthogonal to bracket style: a
//     Kings tournament can be classic1v1 OR ffaGauntlet, same as Free.

// ── THE DIAL (caP'n, 2026-08-18) ─────────────────────────────────────
// X4 goes to the bottom of the list — "on pause for a long time, focus
// on what makes Terror release-ready and full." The bracket is BUILT and
// proven, so nothing here is deleted or reverted; it simply does not
// appear.
//
// V'AEL'S CALL, and it is a one-word revert: the tab is OFF rather than
// merely deprioritised. A Brackets tab that cannot start a duel is
// exactly the kind of half-finished surface that makes a release feel
// unfinished — the code being correct does not help a player who taps it
// and finds AWAITING THE PILOT. Replays stay ON: that one is whole.
//
// ── 2026-08-22 · THE PILOT LANDS ─────────────────────────────────────
// Plaza.js now registers a real setPairingStarter on mount (creates a
// live match with the clicking player's own deck), and TournamentBoard
// seats every OTHER entrant in that pairing automatically the moment
// their result node grows a `code` — the same code-then-join rendezvous
// every human match in this game has always used, just triggered by a
// bracket instead of a kiosk screen. PLAY → ENTER SHATTERFIELD is a real
// path now, so the tab stops lying about what it can do. Reporting a
// result back to the bracket is still a hand tap on the victory screen's
// way out (MatchReporter) — that was never the unfinished part, and
// stays exactly as useful as it's always been either way.
//
// What is STILL not exercised: a real multi-account playtest. Every
// piece here was written and parse/dev-server-verified by one signed-in
// session; two-or-more-real-accounts-at-once is the one thing that
// genuinely needs caP'n's own hands (or a friend's) before this is
// truly proven live, not just correct on paper. Flip back to `false`
// in one word if that playtest finds the seam wrong.
export const SHOW_TOURNAMENTS = true;

// Sizes the bracket is offered at. Not a limit on the maths — buildRound0
// handles any entrant count including odd ones — a limit on the LOBBY, so
// a tournament nobody can fill is never created. THE EXCEPTION LAW: this
// is the set of sizes that exist today, not the set that can exist.
export const BRACKET_SIZES = [4, 8, 16, 32];

// ── BRACKET STYLES ─────────────────────────────────────────────────────
// classic1v1: unchanged, everything above and below still describes it.
// ffaGauntlet: NEW. Valid ONLY at 4 or 8 entrants — an FFA table has no
// bye seat the way a 1v1 pairing does (a walkover is "the other player
// didn't show," which means nothing when the match is 4-wide), so unlike
// classic1v1 a Gauntlet tournament must fill to an exact size before it
// can lock at all (see lockBracket).
//   4 entrants: ONE round — a single 4-seat FFA. Its winner is champion.
//   8 entrants: round 0 is TWO 4-seat FFA pools in parallel (winner-take-
//     all per pool, not top-2); round 1 is the final — the two pool
//     winners, 1v1. See deriveGauntletBracket for the seeding and the
//     3rd-place convention, which is a genuine design call, documented
//     where it's made rather than here.
export const BRACKET_STYLES = { CLASSIC: "classic1v1", GAUNTLET: "ffaGauntlet" };

// Sizes offered per style — same LOBBY-not-MATHS distinction BRACKET_SIZES
// already makes (THE EXCEPTION LAW: today's menu, not a hard ceiling).
export const GAUNTLET_SIZES = [4, 8];
export const SIZES_BY_STYLE = {
  [BRACKET_STYLES.CLASSIC]: BRACKET_SIZES,
  [BRACKET_STYLES.GAUNTLET]: GAUNTLET_SIZES,
};

// THE VYREN PATTERN applied to old data: a v1 tournament (or any caller)
// that has never heard of bracketStyle gets classic1v1 back, byte-
// identical to what this file did before it grew a second style.
export const normalizeBracketStyle = (s) =>
  s === BRACKET_STYLES.GAUNTLET ? BRACKET_STYLES.GAUNTLET : BRACKET_STYLES.CLASSIC;

// ── TOURNAMENT KIND — orthogonal to bracket style ─────────────────────
// free:  deckMode "open" on every match this tournament creates, no
//        entrance fee — "cheater decks allowed" per caP'n.
// kings: deckMode "canon" on every match, REQUIRES an entrance fee paid
//        in Chocolate Coins (THE TWO-CURRENCY LAW — ◕ is Terror's own
//        earnable money; Divine Crests ◆ are the Empire-wide reserve and
//        a buy-in is never charged in them). Winnings split 60/30/10 —
//        see PAYOUT_SPLIT / splitPot.
export const KIND = { FREE: "free", KINGS: "kings" };

// A string the caller/UI reads, never switched on outside this file
// (THE EXCEPTION LAW) — but createMatch's opts.deckMode DOES gate real
// behavior downstream (RulesEnforcer, not wired yet), so the mapping
// lives in exactly one place.
export const deckModeForKind = (kind) => (kind === KIND.KINGS ? "canon" : "open");

// "winner get's most, 2nd place gets some, 3rd place gets a little" —
// caP'n's own ratio, made concrete. Today's numbers, not a law of nature;
// a coordinator-tunable split is a fair future ask, not a rewrite.
export const PAYOUT_SPLIT = { first: 0.6, second: 0.3, third: 0.1 };

// Splits a Chocolate-Coin pot three ways. `hasThird` comes from whether
// THIS bracket shape produced a real 3rd (see each derive*Bracket's
// `.third`) — a 4-entrant Gauntlet and a classic bracket (no bronze
// match) never do. Rounds so the WHOLE pot is always paid out — the
// last seat's share is `pot - the others`, never a separate rounded
// number, so a coin can't vanish or get conjured to rounding.
export function splitPot(pot, hasThird) {
  const whole = Math.floor(pot) || 0;
  if (whole <= 0) return { first: 0, second: 0, third: 0 };
  if (hasThird) {
    const first = Math.round(whole * PAYOUT_SPLIT.first);
    const second = Math.round(whole * PAYOUT_SPLIT.second);
    return { first, second, third: whole - first - second };
  }
  // No 3rd to pay. THE CALL: fold the unclaimed 10% into 1st/2nd
  // PROPORTIONALLY to their own split (60:30 stays a 2:1 ratio, i.e.
  // 66.67:33.33) rather than dumping it all on 1st — 2nd place fought
  // through just as many rounds as 1st and a missing bronze match isn't
  // 2nd's fault.
  const denom = PAYOUT_SPLIT.first + PAYOUT_SPLIT.second;
  const first = Math.round(whole * (PAYOUT_SPLIT.first / denom));
  return { first, second: whole - first, third: 0 };
}

export const STATUS = {
  OPEN: "open",         // taking entrants
  RUNNING: "running",   // bracket locked, duels being played
  DONE: "done",         // a champion exists
};

// ── PURE BRACKET MATHS ────────────────────────────────────────────────

// The smallest power of two that holds n. 0 and 1 are degenerate and
// return themselves rather than throwing: a one-entrant tournament is a
// silly thing to make, not a crash.
export const bracketSize = (n) => {
  if (n <= 1) return n;
  let p = 1;
  while (p < n) p *= 2;
  return p;
};

// The seed order of a full bracket of `size`, as an array of seed
// numbers reading top to bottom. Built by the standard fold: [1,2]
// becomes [1,4,3,2], becomes [1,8,5,4,3,6,7,2]. Each fold pairs every
// existing seed with its complement, which is what guarantees seeds 1
// and 2 can only meet in the final.
export function seedOrder(size) {
  if (size < 2) return size === 1 ? [1] : [];
  let order = [1, 2];
  while (order.length < size) {
    const n = order.length * 2;
    const next = [];
    for (const s of order) { next.push(s); next.push(n + 1 - s); }
    order = next;
  }
  return order;
}

// Round 0, byes included. `entrants` is [{ uid, name, seed }] in ANY
// order; seeds are 1-based and need not be contiguous — they are ranked
// and re-numbered, so a lobby can seed by ladder score without doing
// arithmetic first.
export function buildRound0(entrants) {
  const ranked = [...(entrants || [])]
    .sort((a, b) => (a.seed ?? 1e9) - (b.seed ?? 1e9))
    .map((e, i) => ({ ...e, seed: i + 1 }));
  const size = bracketSize(ranked.length);
  if (size < 2) return [];

  const bySeed = new Map(ranked.map((e) => [e.seed, e]));
  const order = seedOrder(size);
  const matches = [];
  for (let i = 0; i < size; i += 2) {
    const a = bySeed.get(order[i]) || null;      // null = a bye slot
    const b = bySeed.get(order[i + 1]) || null;
    matches.push({ a, b });
  }
  return matches;
}

// A pairing with exactly one player is a WALKOVER: that player advances
// with no duel. A pairing with neither is a dead slot, which happens in
// deep brackets with few entrants and must not become a phantom match.
export const walkoverWinner = (m) => {
  if (m.a && !m.b) return m.a;
  if (m.b && !m.a) return m.b;
  return null;
};

// ── THE DERIVATION ────────────────────────────────────────────────────
// The whole bracket, from entrants + the flat results map. PURE. Call it
// on every render; it is a few dozen array operations.
//
// A result is only consulted for a pairing that HAS two players. A
// stored result naming somebody who is not in the pairing is ignored
// rather than obeyed — a corrupted or stale report must not be able to
// advance a stranger.
// The classic 1v1 single-elimination derivation — COMPLETELY UNCHANGED
// from before the FFA-Gauntlet style existed, just renamed so it can sit
// beside deriveGauntletBracket. `deriveBracket` below is the public
// dispatcher both styles are reached through.
export function deriveClassicBracket(entrants, results = {}) {
  const rounds = [];
  let current = buildRound0(entrants);
  if (!current.length) return { rounds, style: BRACKET_STYLES.CLASSIC, champion: null, runnerUp: null, third: null, size: 0 };

  let r = 0;
  while (current.length >= 1) {
    const matches = current.map((m, i) => {
      const key = `r${r}m${i}`;
      const wo = walkoverWinner(m);
      let winner = null;
      if (wo) winner = wo;
      else if (m.a && m.b) {
        const reported = results[key]?.winner;
        if (reported === m.a.uid) winner = m.a;
        else if (reported === m.b.uid) winner = m.b;
      }
      return {
        key, round: r, index: i,
        a: m.a, b: m.b,
        seats: [m.a, m.b], seatCount: 2, teams: null, // the generic shape liveMatches/myMatch/standings/pairingOpts read across BOTH bracket styles
        walkover: !!wo,
        live: !!(m.a && m.b) && !winner,
        winner,
        code: results[key]?.code || null,
        at: results[key]?.at || null,
      };
    });
    rounds.push(matches);

    if (matches.length === 1) break;          // that was the final

    const next = [];
    for (let i = 0; i < matches.length; i += 2) {
      next.push({ a: matches[i]?.winner || null, b: matches[i + 1]?.winner || null });
    }
    current = next;
    r++;
  }

  const finalMatch = rounds[rounds.length - 1]?.[0] || null;
  const champion = finalMatch?.winner || null;
  let runnerUp = null;
  if (champion && finalMatch) {
    runnerUp = finalMatch.a && finalMatch.a.uid !== champion.uid ? finalMatch.a
             : finalMatch.b && finalMatch.b.uid !== champion.uid ? finalMatch.b
             : null;
  }
  return {
    rounds,
    style: BRACKET_STYLES.CLASSIC,
    size: bracketSize((entrants || []).length),
    champion,
    runnerUp,
    // No bronze match exists in a classic single-elimination bracket — a
    // clean, unique 3rd would need a semifinal-losers playoff this system
    // has never run. Left null on purpose; splitPot() folds a Kings
    // tournament's unclaimed 10% into 1st/2nd rather than guessing which
    // semifinal loser "deserves" bronze.
    third: null,
  };
}

// ── THE FFA-GAUNTLET DERIVATION ───────────────────────────────────────
// Same "immutable rounds, derived from results so far" discipline as the
// classic bracket, just with wider matches. `entrants` must number 4 or
// 8 (GAUNTLET_SIZES) — anything else returns the same empty shape a
// degenerate classic bracket does, on purpose, so a caller never has to
// special-case "this style rejected my input" differently per style.
export function buildGauntletRound0(entrants) {
  const ranked = [...(entrants || [])]
    .sort((a, b) => (a.seed ?? 1e9) - (b.seed ?? 1e9))
    .map((e, i) => ({ ...e, seed: i + 1 }));
  const n = ranked.length;
  if (n === 4) return [{ seats: ranked }];       // one pool — it IS the tournament
  if (n === 8) {
    // Two pools of four, snake-seeded the same way a group stage always
    // is: pool A takes seeds 1,4,5,8, pool B takes 2,3,6,7. That keeps
    // seeds 1 and 2 — the likeliest finalists — in DIFFERENT pools, the
    // same "1 and 2 can only meet in the final" guarantee seedOrder gives
    // the classic bracket, extended one level down to a group stage.
    const bySeed = new Map(ranked.map((e) => [e.seed, e]));
    const poolA = [1, 4, 5, 8].map((s) => bySeed.get(s));
    const poolB = [2, 3, 6, 7].map((s) => bySeed.get(s));
    return [{ seats: poolA }, { seats: poolB }];
  }
  return []; // not a valid Gauntlet size
}

export function deriveGauntletBracket(entrants, results = {}) {
  const groups = buildGauntletRound0(entrants);
  if (!groups.length) {
    return { rounds: [], style: BRACKET_STYLES.GAUNTLET, champion: null, runnerUp: null, third: null, size: 0 };
  }

  // Reads one match's stored result, but ONLY if it actually names a
  // seat that's IN this match — same "a corrupted or stale report must
  // not be able to advance a stranger" rule the classic bracket applies,
  // extended to a 4-wide pool with two named fields (winner, runnerUp)
  // instead of one.
  const readMatch = (key, seats) => {
    const r = results[key] || {};
    const bySeat = new Map(seats.filter(Boolean).map((e) => [e.uid, e]));
    const winner = bySeat.get(r.winner) || null;
    const runnerUp = (r.runnerUp && r.runnerUp !== r.winner) ? (bySeat.get(r.runnerUp) || null) : null;
    // R98 — surfaced, never advanced. See reportResult's banner.
    const coWinners = (r.coWinners || []).map((u) => bySeat.get(u)).filter(Boolean);
    return { winner, runnerUp, coWinners, code: r.code || null, at: r.at || null };
  };

  const round0 = groups.map((g, i) => {
    const key = `r0m${i}`;
    const { winner, runnerUp, coWinners, code, at } = readMatch(key, g.seats);
    return {
      key, round: 0, index: i, seats: g.seats, seatCount: g.seats.length, teams: null,
      live: !winner, winner, runnerUp, coWinners, code, at,
    };
  });

  if (round0.length === 1) {
    // 4-entrant Gauntlet — one FFA pool IS the whole tournament.
    const m = round0[0];
    return {
      rounds: [round0],
      style: BRACKET_STYLES.GAUNTLET,
      size: 4,
      champion: m.winner,
      runnerUp: m.runnerUp,
      // No natural 3rd out of a single 4-seat pool beyond the runner-up
      // — there is nothing left to derive it from (the FFA reports one
      // winner and one runner-up; the other two seats are just "out"),
      // so it is left unawarded rather than guessed at. splitPot() folds
      // the unclaimed 10% back into 1st/2nd for exactly this case.
      third: null,
    };
  }

  // 8-entrant Gauntlet — round 1 is the final: the two pool winners, 1v1.
  const finalSeats = [round0[0].winner, round0[1].winner];
  const fKey = "r1m0";
  const { winner: finalWinner, code: fCode, at: fAt } = readMatch(fKey, finalSeats);
  const final = {
    key: fKey, round: 1, index: 0, seats: finalSeats, seatCount: 2, teams: null,
    live: !!(finalSeats[0] && finalSeats[1]) && !finalWinner,
    winner: finalWinner, runnerUp: null, code: fCode, at: fAt,
  };
  // The final is a 2-seat match — its loser IS 2nd place outright, no
  // separate report needed the way a 4-seat FFA needs a runnerUp field.
  if (finalWinner) {
    final.runnerUp = finalSeats.find((s) => s && s.uid !== finalWinner.uid) || null;
  }

  // ── THIRD PLACE — THE CALL CAP'N ASKED FOR OUT LOUD ("gotta think
  // out of the box") ──────────────────────────────────────────────────
  // An 8-player Gauntlet has six players who never reached the final,
  // and only ONE of them carries a result that ranks them at all: the
  // runnerUp of whichever pool produced the eventual 2nd-place finisher
  // — i.e. the strongest player THAT finalist personally had to beat to
  // get there. The other pool's runner-up is just as plausible a paper
  // "3rd," but tying 3rd to the eventual runner-up's own pool keeps a
  // single, defensible line of ancestry: 1st beat 2nd in the final, and
  // 2nd already beat 3rd once, in the pool that sent them both forward.
  // That is the story this convention tells; it is a design call, not a
  // derivation, and it is written down here because there was no
  // mathematically "more correct" answer to go find instead.
  let third = null;
  if (final.winner && final.runnerUp) {
    const losingPool = round0.find((m) => m.winner && m.winner.uid === final.runnerUp.uid);
    third = losingPool ? losingPool.runnerUp : null;
  }

  return {
    rounds: [round0, [final]],
    style: BRACKET_STYLES.GAUNTLET,
    size: 8,
    champion: final.winner,
    runnerUp: final.runnerUp,
    third,
  };
}

// The public dispatcher — the one both files import. Old 2-arg call
// sites (bracketStyle omitted) resolve to classic1v1 via
// normalizeBracketStyle, so every existing caller keeps working exactly
// as it did before this file had a second style.
export function deriveBracket(entrants, results = {}, bracketStyle) {
  return normalizeBracketStyle(bracketStyle) === BRACKET_STYLES.GAUNTLET
    ? deriveGauntletBracket(entrants, results)
    : deriveClassicBracket(entrants, results);
}

// Every pairing that is ready to be played right now — both seats
// filled, no result yet. This is the list a lobby screen turns into
// "PLAY YOUR MATCH" buttons.
export const liveMatches = (bracket) =>
  (bracket?.rounds || []).flat().filter((m) => m.live);

// The one a given player owes. Reads `m.seats` (every match in both
// styles carries one) rather than assuming exactly two named slots —
// a Gauntlet round-0 pool has four.
export const myMatch = (bracket, uid) =>
  liveMatches(bracket).find((m) => (m.seats || []).some((s) => s?.uid === uid)) || null;

// Where each entrant went out — for a standings table that reads like a
// story instead of a list of losers. Generalized over `m.seats` so a
// 4-wide FFA pool logs all three non-winners as "out in round 0" the
// same pass that a classic 2-seat match logs its one loser — nothing
// here assumes a match has exactly two slots.
export function standings(bracket) {
  const out = new Map();
  for (const round of bracket?.rounds || []) {
    for (const m of round) {
      if (!m.winner) continue;
      for (const s of m.seats || []) {
        if (!s || s.uid === m.winner.uid || out.has(s.uid)) continue;
        out.set(s.uid, { ...s, outIn: m.round, runnerUp: !!(m.runnerUp && s.uid === m.runnerUp.uid) });
      }
    }
  }
  if (bracket?.champion) out.set(bracket.champion.uid, { ...bracket.champion, outIn: null, champion: true });
  return [...out.values()].sort((x, y) => {
    if (x.champion) return -1;
    if (y.champion) return 1;
    return (y.outIn ?? -1) - (x.outIn ?? -1);
  });
}

// `style` is optional and defaults to classic — every existing 2-arg
// call site keeps naming rounds exactly as it did before the Gauntlet
// style existed.
export const roundName = (roundIdx, totalRounds, style = BRACKET_STYLES.CLASSIC) => {
  if (normalizeBracketStyle(style) === BRACKET_STYLES.GAUNTLET) {
    if (totalRounds <= 1) return "The Gauntlet"; // 4-entrant: one round, the whole tournament
    return roundIdx === 0 ? "The Gauntlet" : "The Final";
  }
  const fromEnd = totalRounds - 1 - roundIdx;
  if (fromEnd === 0) return "The Final";
  if (fromEnd === 1) return "Semifinal";
  if (fromEnd === 2) return "Quarterfinal";
  return `Round ${roundIdx + 1}`;
};

// ── FIREBASE ──────────────────────────────────────────────────────────

export async function createTournament(uid, {
  name, size = 8, mode = "open", bracketStyle, kind = KIND.FREE, entranceFee,
} = {}) {
  if (!uid) return { ok: false, reason: "no-uid" };

  const style = normalizeBracketStyle(bracketStyle);

  // Size validation deliberately splits by style. classic1v1's fallback
  // (silently clamp to 8) is UNCHANGED — untouched per the brief. A
  // Gauntlet's table can't silently resize the same way: there's no bye
  // seat to soak up the difference, so an off-menu size (3, 5, 6, 7...)
  // is rejected outright rather than quietly rounded to something the
  // caller didn't ask for.
  let safeSize;
  if (style === BRACKET_STYLES.GAUNTLET) {
    if (!GAUNTLET_SIZES.includes(size)) return { ok: false, reason: "bad-gauntlet-size" };
    safeSize = size;
  } else {
    safeSize = BRACKET_SIZES.includes(size) ? size : 8; // byte-identical to pre-Gauntlet behavior
  }

  const safeKind = kind === KIND.KINGS ? KIND.KINGS : KIND.FREE;
  let fee = null;
  if (safeKind === KIND.KINGS) {
    // "up to coordinator to set... must be a positive integer" — no
    // economy simulation, just a sane buy-in. THE TWO-CURRENCY LAW: a
    // Kings entrance fee is always chocolateCoins — never divineCrests,
    // never a caller-supplied currency string.
    const amount = Math.floor(Number(entranceFee?.amount));
    if (!(amount > 0)) return { ok: false, reason: "entrance-fee-required" };
    fee = { amount, currency: "chocolateCoins" };
  }
  // A Free tournament never carries a fee, even if one was accidentally
  // passed in — the kind is the source of truth, not the caller's typo.

  const id = `t${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  try {
    await set(ref(db, `${GAME_BASE}/tournaments/${id}`), {
      meta: {
        v: TOURNAMENT_SCHEMA,
        name: name || "A Solrend Tournament",
        createdBy: uid, createdAt: Date.now(),
        size: safeSize,
        bracketStyle: style,
        kind: safeKind,
        entranceFee: fee,              // null for Free Tournaments
        status: STATUS.OPEN, mode,
      },
      entrants: {}, results: {},
    });
    return { ok: true, id };
  } catch (e) {
    console.warn("[tournament] create failed:", e?.message);
    return { ok: false, reason: "write-failed" };
  }
}

// Join. Transaction-guarded so two players cannot take the same last
// slot — the seed is assigned INSIDE the transaction from the entrant
// count, which is the only place it can be assigned without a race.
//
// ⚠ SEEDING IS ARRIVAL ORDER TODAY. First in is seed 1, and that is a
// PLACEHOLDER, not a design — it means the bracket is fair in shape and
// arbitrary in who gets the bye. The seam is already here: pass
// `seedHint` and the caller decides. Ladder score (profiles/{uid}/pvp/
// score) is the obvious first one and is a lobby-side read, not a change
// in this file. Left as a caP'n call because "who deserves the bye" is a
// design question, not a number.
// A Kings tournament's buy-in is charged HERE, once, at join — not once
// per bracket match. A player who reaches the final of an 8-entrant
// Gauntlet plays TWO matches inside one ticket; billing entranceFee at
// every match createMatch produces would charge them 2-3 buy-ins for
// one. See pairingOpts below for the other half of that decision (why a
// tournament's own createMatch calls never carry opts.entranceFee).
export async function joinTournament(id, uid, name, seedHint = null) {
  if (!id || !uid) return { ok: false, reason: "bad-args" };
  let fee = 0;
  let charged = false;
  try {
    const metaSnap = await get(ref(db, `${GAME_BASE}/tournaments/${id}/meta`));
    const meta = metaSnap.val();
    if (!meta) return { ok: false, reason: "no-such-tournament" };
    if (meta.status !== STATUS.OPEN) return { ok: false, reason: "closed" };

    fee = meta.kind === KIND.KINGS ? (meta.entranceFee?.amount || 0) : 0;
    if (fee > 0) {
      const paid = await spendCoins(uid, fee); // awardCoins/spendCoins — the same ◕ lane every other Terror faucet/sink uses
      if (!paid) return { ok: false, reason: "insufficient-coins" };
      charged = true;
    }

    let outcome = "joined";
    await runTransaction(ref(db, `${GAME_BASE}/tournaments/${id}/entrants`), (cur) => {
      const e = cur || {};
      if (e[uid]) { outcome = "already-in"; return e; }
      if (Object.keys(e).length >= meta.size) { outcome = "full"; return e; }
      e[uid] = { name: name || "Aenarchist", seed: seedHint ?? (Object.keys(e).length + 1), joinedAt: Date.now() };
      return e;
    });

    // AUDIT FIX (2026-08-22): the entrant transaction above only locks
    // against ANOTHER JOINER — it can't see `meta/status` (a sibling
    // ref), so a lockBracket() landing between the OPEN check up top and
    // this transaction's commit could still seat a "late" entrant into a
    // bracket that has already been derived and is being played. Close
    // that window here: re-read status right after committing, and if
    // it moved off OPEN while we were racing, undo the seat exactly like
    // the already-in/full paths below undo the charge — a bracket that
    // reshuffles because somebody joined after lock is not a bracket.
    if (outcome === "joined") {
      const recheck = await get(ref(db, `${GAME_BASE}/tournaments/${id}/meta/status`));
      if (recheck.val() !== STATUS.OPEN) {
        await runTransaction(ref(db, `${GAME_BASE}/tournaments/${id}/entrants`), (cur) => {
          const e = { ...(cur || {}) };
          delete e[uid];
          return e;
        });
        outcome = "locked";
      }
    }

    // The entrant transaction can land on "already-in", "full" or
    // "locked" for reasons that have nothing to do with payment (a race
    // against another joiner or against a lock, a stale click) — in
    // every case the fee we just took bought this uid nothing, so it
    // goes straight back. A Kings tournament's pot is exactly (entrants
    // who are actually seated) × (entranceFee.amount) BECAUSE of this
    // refund — nobody who paid is ever left both uncharged-back and
    // unseated.
    if (fee > 0 && outcome !== "joined") {
      await awardCoins(uid, fee, `tournament_${id}_entry_refund`);
    }

    if (outcome === "full") return { ok: false, reason: "full" };
    if (outcome === "locked") return { ok: false, reason: "locked" };
    return { ok: true, reason: outcome };
  } catch (e) {
    console.warn("[tournament] join failed:", e?.message);
    // AUDIT FIX (2026-08-22): a throw ANYWHERE past the spendCoins call
    // (the entrant transaction included) used to strand a charged fee —
    // the refund logic above only runs when the try block reaches it
    // normally. Always try to make the player whole before giving up.
    if (fee > 0 && charged) {
      try { await awardCoins(uid, fee, `tournament_${id}_entry_refund_error`); }
      catch (e2) { console.warn("[tournament] refund-on-error also failed:", e2?.message); }
    }
    return { ok: false, reason: "write-failed" };
  }
}

// Lock the bracket. From here the entrant list is frozen, which is what
// makes deriveBracket's output stable — a bracket that reshuffles because
// somebody joined in round two is not a bracket.
//
// classic1v1 keeps its original one-line behavior exactly (any host, any
// entrant count ≥2, whatever the UI already gates). ffaGauntlet adds ONE
// new refusal: an FFA pool has no bye seat, so unlike a classic bracket
// (happy to lock at 5-of-8 and hand out byes) a Gauntlet must fill to
// its exact announced size before it can start at all.
export const lockBracket = async (id) => {
  try {
    const snap = await get(ref(db, `${GAME_BASE}/tournaments/${id}`));
    const t = snap.val();
    if (!t?.meta) return false;
    if (normalizeBracketStyle(t.meta.bracketStyle) === BRACKET_STYLES.GAUNTLET) {
      const entrantCount = Object.keys(t.entrants || {}).length;
      if (entrantCount !== t.meta.size) return false; // not full — an FFA table can't start short-handed
    }
    await update(ref(db, `${GAME_BASE}/tournaments/${id}/meta`), { status: STATUS.RUNNING, lockedAt: Date.now() });
    return true;
  }
  catch (e) { console.warn("[tournament] lock failed:", e?.message); return false; }
};

// Report a result. ONE write at a known path — see the storage note.
// `runnerUpUid`, new, is who placed 2nd IN THIS MATCH — meaningless for
// a classic 2-seat pairing (the loser already says that), but it's the
// only signal a Gauntlet round-0 FFA pool carries for who its OWN 2nd
// place was (see deriveGauntletBracket's 3rd-place convention). Absent
// for every call that doesn't pass it, same as gameManager.endMatch's
// runnerUp param this mirrors.
// ── R98 · A BRACKET ADVANCES ONE ENTRANT, AND SAYS SO ────────────────
// caP'n, 2026-08-25: a Solrend can end with more than one winner.
// A BRACKET SLOT CANNOT. Round 0 of a Gauntlet feeds exactly one
// entrant into round 1, and inventing a second would either hand one
// pool two seats in the final or silently drop somebody — both worse
// than the thing being modelled.
//
// So the ruling is HONOURED WITHOUT BEING PRETENDED: `winner` still
// names the single entrant that advances (the PRIMARY/board winner,
// exactly as endMatch stores it), and `coWinners` records everyone
// else who also won that Solrend so the archive, the payout screen and
// any future "both were champions" display can see it. A co-winner in a
// tournament won their duel; they did not win the bracket seat.
export async function reportResult(id, matchKey, winnerUid, code = null, runnerUpUid = null, coWinnerUids = null) {
  if (!id || !matchKey || !winnerUid) return false;
  try {
    const payload = { winner: winnerUid, code: code || null, at: Date.now() };
    if (runnerUpUid) payload.runnerUp = runnerUpUid;
    const co = (coWinnerUids || []).filter((u) => u && u !== winnerUid);
    if (co.length) payload.coWinners = co;
    await set(ref(db, `${GAME_BASE}/tournaments/${id}/results/${matchKey}`), payload);
    return true;
  } catch (e) {
    console.warn("[tournament] report failed:", e?.message);
    return false;
  }
}

// ── PAYOUT — KINGS TOURNAMENTS ────────────────────────────────────────
// Runs once a champion exists. Guarded by a transaction on meta.status
// (RUNNING → DONE) so two clients both noticing "hey, the bracket's
// finished" at once can't both pay out — only the caller whose
// transaction actually flips the status gets `won: true` back and goes
// on to compute/credit the split; the loser of that race just learns
// the tournament was already settled.
//
// `entrantCount` is passed in rather than re-derived here because the
// caller (TournamentBoard) already has it from entrantList(data.entrants)
// — and because it must be the ACTUAL seated count, not `meta.size`,
// even though the two are identical for every Gauntlet (lockBracket
// enforces the exact-fill above) and are supposed to match for classic
// too by the time a bracket locks.
export async function finalizeTournament(id, bracket, entrantCount) {
  if (!id || !bracket?.champion) return { ok: false, reason: "not-decided" };
  try {
    const metaRef = ref(db, `${GAME_BASE}/tournaments/${id}/meta`);
    let won = false;
    const result = await runTransaction(metaRef, (meta) => {
      if (!meta) return meta;                 // tournament vanished — abort
      if (meta.status === STATUS.DONE) return; // abort — already paid, someone else won the race
      won = true;
      return { ...meta, status: STATUS.DONE, decidedAt: Date.now() };
    });
    if (!result.committed || !won) return { ok: false, reason: "already-done" };

    const meta = result.snapshot.val();
    if (meta.kind !== KIND.KINGS) return { ok: true, paid: null }; // Free tournaments have no pot to pay

    const pot = meta.entranceFee?.amount > 0 ? entrantCount * meta.entranceFee.amount : 0;
    const split = splitPot(pot, !!bracket.third);
    const payout = {};
    if (bracket.champion) payout[bracket.champion.uid] = (payout[bracket.champion.uid] || 0) + split.first;
    if (bracket.runnerUp) payout[bracket.runnerUp.uid] = (payout[bracket.runnerUp.uid] || 0) + split.second;
    if (bracket.third) payout[bracket.third.uid] = (payout[bracket.third.uid] || 0) + split.third;

    // Written to the tournament's own node FIRST and regardless of what
    // happens below — this is the ledger of what SHOULD be paid, and it
    // must survive even if a credit call beneath it throws partway
    // through, so a payout is never silently lost from the record.
    await update(ref(db, `${GAME_BASE}/tournaments/${id}`), { payout, pot });

    // Actually credit it — awardCoins is the one lane every other ◕
    // faucet in Terror already uses (delta transaction + audit row,
    // never a full-profile overwrite), so a tournament payout looks
    // exactly like a Depths win or a Sweet Treat grant to the ledger.
    for (const [payeeUid, amount] of Object.entries(payout)) {
      if (amount > 0) await awardCoins(payeeUid, amount, `tournament_${id}_payout`);
    }
    return { ok: true, paid: payout };
  } catch (e) {
    console.warn("[tournament] finalize failed:", e?.message);
    return { ok: false, reason: "write-failed" };
  }
}

export const listenToTournament = (id, cb) => {
  if (!id || typeof cb !== "function") return () => {};
  return onValue(ref(db, `${GAME_BASE}/tournaments/${id}`), (s) => cb(s.val() || null), () => cb(null));
};

export const listOpenTournaments = async () => {
  try {
    const snap = await get(ref(db, `${GAME_BASE}/tournaments`));
    const val = snap.val() || {};
    return Object.entries(val)
      .map(([id, t]) => ({ id, ...(t.meta || {}), entrants: Object.keys(t.entrants || {}).length }))
      .filter((t) => t.status !== STATUS.DONE)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } catch (e) {
    console.warn("[tournament] list failed:", e?.message);
    return [];
  }
};

// Entrants as deriveBracket wants them.
export const entrantList = (entrants) =>
  Object.entries(entrants || {}).map(([uid, e]) => ({ uid, name: e.name, seed: e.seed }));

// ── THE NAMED SEAM ────────────────────────────────────────────────────
// The one thing X4 still owes. A pairing becomes a duel when somebody
// creates a match and hands the code to both seats; that is
// createMatch + joinMatch + a rendezvous, and the rendezvous is the part
// that needs two real accounts in two real browsers to get right.
//
// This is a hook, not a stub that pretends: a caller supplies the
// starter, the bracket calls it, and until one is supplied a pairing
// says "waiting on the pilot" instead of showing a button that lies.
// Turns a tournament's meta (bracketStyle/kind) plus one derived match
// into EXACTLY the `opts` bag gameManager.createMatch expects, so the
// starter function — whenever it's wired — has zero bracket-shape logic
// of its own to write: it gets `seats` (uid/name/seed, in seat order)
// and a ready-made opts object, and just calls
// createMatch(name, deck…, opts) once per seat that needs seating.
//
// opts.entranceFee is DELIBERATELY never set here. A Kings buy-in is
// charged ONCE, at joinTournament — see the comment there. Setting it
// again on every bracket match would either double/triple-bill an
// entrant across their rounds, or (if the starter took it at face value
// and called spendCoins itself) silently duplicate a charge the bridge
// has no way to know was already collected. The starter needs nothing
// more than deckMode to know which rules apply to the match; the money
// question is already settled by the time a pairing exists.
export function pairingOpts(meta, match) {
  const style = normalizeBracketStyle(meta?.bracketStyle);
  const seatCount = match?.seats?.length || 2;
  const teams = match?.teams || null; // no teamed tournament style exists yet — the field is here so one can arrive without a second seam
  const mode = style === BRACKET_STYLES.GAUNTLET && seatCount > 2 ? "ffa4" : "1v1";
  const opts = { seatCount, mode, deckMode: deckModeForKind(meta?.kind) };
  if (teams) opts.teams = teams;
  return opts;
}

let _starter = null;
export const setPairingStarter = (fn) => { _starter = typeof fn === "function" ? fn : null; };
export const canStartPairings = () => !!_starter;
// `meta` is the tournament's meta object (TournamentBoard already holds
// it from listenToTournament) — pairingOpts turns it + `match` into the
// createMatch opts the starter needs. Optional and defaulted to {} so a
// caller mid-migration that still passes only (id, match) doesn't throw;
// deckModeForKind(undefined) resolves to "open", the safest default.
export async function onPairingReady(id, match, meta = {}) {
  if (!_starter) return { ok: false, reason: "no-starter" };
  const resultRef = ref(db, `${GAME_BASE}/tournaments/${id}/results/${match.key}`);
  // AUDIT FIX (2026-08-22): PLAY is shown to EVERY seated entrant in a
  // live pairing, not just a host — nothing here restricts who taps it.
  // Two entrants tapping within the same tick used to both fall through
  // to `_starter`, each running its own createMatch: two real matches,
  // with the second `code` write silently clobbering the first's, so
  // the bracket only ever remembers one while some seated entrants can
  // be in the orphaned other. Claim the right to start THIS pairing with
  // a transaction before `_starter` (which has side effects and can't
  // safely live inside a transaction updater) ever runs; whoever loses
  // the claim backs off — the code they need still reaches them through
  // the same results listener the UI already watches once the winner
  // writes it. A stale claim (starter died mid-flight, tab closed) self-
  // heals after STALE_CLAIM_MS rather than wedging the pairing forever.
  const STALE_CLAIM_MS = 20000;
  let won = false;
  try {
    await runTransaction(resultRef, (cur) => {
      if (cur?.code) { won = false; return cur; }
      const stale = cur?.starting && cur?.startingAt && (Date.now() - cur.startingAt > STALE_CLAIM_MS);
      if (cur?.starting && !stale) { won = false; return cur; }
      won = true;
      return { ...(cur || {}), starting: true, startingAt: Date.now() };
    });
    if (!won) return { ok: false, reason: "already-starting" };

    const opts = pairingOpts(meta, match);
    const code = await _starter(match, opts);
    if (code) {
      await update(resultRef, { code, starting: null });
      return { ok: true, code };
    }
    // The starter declined (no resolvable deck, etc.) — release the
    // claim so a later attempt can try again instead of finding this
    // pairing permanently marked "starting".
    await update(resultRef, { starting: null });
    return { ok: false, reason: "starter-declined" };
  } catch (e) {
    console.warn("[tournament] pairing start failed:", e?.message);
    if (won) {
      try { await update(resultRef, { starting: null }); } catch (_e2) { /* best-effort release */ }
    }
    return { ok: false, reason: "start-failed" };
  }
}

// ── THE FACTION-WARS SEAM (X4's own roadmap note) ─────────────────────
// "faction wars may fold in here." They can, and this is where: a
// tournament with mode "faction" seeds by faction and reports its
// champion's faction into the week's tally. Nothing is built for it yet
// and nothing needs to be — it is a mode string, a seed comparator, and
// one call to factionWars.tallyFactionWin. Written down so the next
// session does not re-derive it.
export const FOLD_IN_NOTE = "mode:'faction' → seed by faction, champion's faction takes the week";

const tournament = {
  TOURNAMENT_SCHEMA, SHOW_TOURNAMENTS, BRACKET_SIZES, STATUS,
  BRACKET_STYLES, GAUNTLET_SIZES, SIZES_BY_STYLE, normalizeBracketStyle,
  KIND, deckModeForKind, PAYOUT_SPLIT, splitPot,
  bracketSize, seedOrder, buildRound0, walkoverWinner,
  deriveClassicBracket, buildGauntletRound0, deriveGauntletBracket, deriveBracket,
  liveMatches, myMatch, standings, roundName,
  createTournament, joinTournament, lockBracket, reportResult, finalizeTournament,
  listenToTournament, listOpenTournaments, entrantList,
  pairingOpts, setPairingStarter, canStartPairings, onPairingReady,
};
export default tournament;
